import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import Tooltip from 'react-simple-tooltip';

import axios from 'axios';
import { Md5 } from 'ts-md5/dist/md5';
import { useAccount, useConnect, useDisconnect } from 'wagmi';

import { useGlobalContext } from '../../AppContext';
import { notify } from '../../utils/utils';
import { Button } from '../Button';

import './WalletButton.scss';

export function WalletButton() {
  const {
    isMobile,
    tutorialCount,
    setTutorialCount,
    demoMode,
    setAddress,
    setPreparing,
    setSigned,
    setWallets,
    setDemoMode,
    setHideMinimumToken,
    setHideMinimumWallet,
    setCustomSearch,
    setConnectedWallets,
  } = useGlobalContext();
  const { connectAsync, connectors, error: errorWagmi, isLoading, pendingConnector } = useConnect();
  const { disconnect } = useDisconnect();
  const { address: account, connector: connectorWagmi, isConnected } = useAccount();

  const [walletConnected, setWalletConnected] = useState<boolean>(false);
  const [buttonLabel, setButtonLabel] = useState<string>('Connect Wallet');
  const [, /*currentAddress*/ setCurrentAddress] = useState<string>('');
  const [suggestions, setSuggestions] = useState(false);
  const [walletData, setWalletData] = useState<any>();
  const [tutorial, setTutorial] = useState<string>('');
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const handleThemeChange = () => {
      const newTheme = document.body.classList.contains('dark') ? 'light' : 'dark';
      setTheme(newTheme);
    };

    handleThemeChange();

    const observer = new MutationObserver(handleThemeChange);
    const config = { attributes: true, attributeFilter: ['class'] };
    observer.observe(document.body, config);

    return () => {
      observer.disconnect();
    };
  }, []);

  const [avatarLink, setAvatarLink] = useState<string>(
    `https://www.gravatar.com/avatar/${Md5.hashStr(account ? account : 'walletscan')}?d=identicon`,
  );

  const { ethereum } = window as any;
  if (typeof ethereum !== 'undefined') {
    //console.log('MetaMask is installed!');
  }

  async function fetchData(account: string): Promise<void> {
    try {
      const response = await axios({
        method: 'post',
        url: `${process.env.REACT_APP_API}/wallet-connect.php`,
        data: {
          address: account,
        },
        withCredentials: true,
      });

      console.log('WalletButton fetchData end', response.data);

      if (response.data.code === 0) {
        setSigned(true);
        setWallets(response.data.data.wallets);
        setWalletData(response.data.data);
        setConnectedWallets(response.data.data.wallets);
        console.log('Connected.', response.data.data);
        setHideMinimumToken(response.data.data.minimumTokenAmount);
        setHideMinimumWallet(response.data.data.minimumWalletAmount);
        const getWallet = document.location.search.split('?q=')[1];
        if (getWallet) {
          setCurrentAddress(getWallet);
          setCustomSearch(true);
          setAddress(getWallet);
          setPreparing(true);
        } else {
          setCurrentAddress(account);
          setCustomSearch(false);
          setAddress(String(account));
        }
      }
    } catch (error) {
      //console.error(error);
    }
  }

  const customDisconnect = () => {
    setAddress('');
    setSuggestions(false);
    setCurrentAddress('');
    disconnect();
  };

  const customConnect = async (connector: any) => {
    await connectAsync({ connector });
    setSuggestions(false);
  };

  useEffect(() => {
    setButtonLabel(
      !isConnected
        ? 'Connect Wallet'
        : account
        ? `${account!.substr(0, 8)}...${account!.substr(-6, 6)}`
        : 'Connecting',
    );
    setAvatarLink(
      `https://www.gravatar.com/avatar/${Md5.hashStr(account || 'walletconnect')}?d=identicon`,
    );
  }, [walletConnected, account]);

  useEffect(() => {
    setCurrentAddress(account || '');
    if (account) {
      setDemoMode(true);
      setWalletConnected(true);
      console.log('fetchData triggered.', account);
      fetchData(account).then(() => {});
    }
  }, [account]);

  useEffect(() => {
    if (tutorialCount === 8) {
      setTutorial('headerWallet');
      setTimeout(() => {
        setTutorialCount(9);
      }, 1000);
    } else if (tutorialCount === 9) {
      setTutorial('headerProfile');
      setTimeout(() => {
        setTutorial('');
        setTutorialCount(10);
      }, 1000);
    }
  }, [tutorialCount]);

  return (
    <>
      {demoMode && tutorialCount !== 0 && (
        <div className="wallet-button">
          <div className="wallet-left">
            <div className="address">
              <>Not connected</>
            </div>
            <button>
              <span>Connect Wallet</span>
              <img alt="" src={`/icons/wallet-${theme}.svg`} />
            </button>
            {demoMode && tutorial === 'headerWallet' && (
              <>
                <div className={`demo-details ${isMobile ? 'mobile' : ''} headerWallet`}>
                  <img src="/tutorial/arrow.svg" width="30" height="70" alt="" />
                  <div className="description">Connect Your Wallet Here</div>
                </div>
              </>
            )}
          </div>
          <div className="wallet-right">
            <div className="avatar">
              <img src={avatarLink} alt="" />
            </div>
            {demoMode && tutorial === 'headerProfile' && (
              <>
                <div className={`demo-details ${isMobile ? 'mobile' : ''} headerProfile`}>
                  <img src="/tutorial/arrow-2.svg" alt="" />
                  <div className="description">Add Your Other Wallet Here</div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {(!demoMode || (demoMode && tutorialCount === 0)) && (
        <div className="wallet-button">
          <div className="wallet-left">
            <div className="address">
              {!isConnected ? (
                'Not connected'
              ) : walletData ? (
                <>
                  {walletData.wallets.length} Wallet(s) in Portfolio
                  <Tooltip
                    placement="bottom"
                    className="tooltip"
                    border="#ff6aff"
                    background="var(--color-background-main)"
                    color="var(--color-text-main)"
                    content={walletData.wallets.map((item: any, index: number) => {
                      return (
                        <div className="wallet-item" key={index}>
                          <div>{item.name} </div>
                          <div>
                            {item.network &&
                              item.network.split(',').map((i: any, ind: number) => {
                                return <img key={ind} src={`/icons/networks/${i}.png`} alt="" />;
                              })}
                          </div>
                        </div>
                      );
                    })}
                  >
                    <div className="question-circle">?</div>
                  </Tooltip>
                </>
              ) : (
                'Connecting'
              )}
            </div>
            <button onClick={() => setSuggestions(!suggestions)}>
              <span>{buttonLabel}</span>
              {connectorWagmi && (
                <img
                  alt=""
                  src={`/icons/${connectorWagmi.id.replace(' ', '_').toLowerCase()}.svg`}
                />
              )}
              {!connectorWagmi && <img alt="" src={`/icons/wallet-${theme}.svg`} />}
            </button>
            {demoMode && tutorial === 'headerWallet' && (
              <>
                <div className={`demo-details ${isMobile ? 'mobile' : ''} headerWallet`}>
                  <img src="/tutorial/arrow.svg" width="30" height="70" alt="" />
                  <div className="description">Connect Your Wallet Here</div>
                </div>
              </>
            )}
          </div>
          <div className="wallet-right">
            <div className="avatar">
              {account ? (
                <NavLink to="/profile">
                  <img src={avatarLink} alt="" />
                </NavLink>
              ) : (
                <img
                  src={avatarLink}
                  onClick={() => {
                    notify('You need to connect your wallet to use this feature.');
                  }}
                  alt=""
                />
              )}
              {false && <span className="badge">5</span>}
            </div>
            {demoMode && tutorial === 'headerProfile' && (
              <>
                <div className={`demo-details ${isMobile ? 'mobile' : ''} headerProfile`}>
                  <img src="/tutorial/arrow-2.svg" alt="" />
                  <div className="description">Add Your Other Wallet Here</div>
                </div>
              </>
            )}
          </div>
          {suggestions && <div className="grayedOut" />}
          {suggestions && (
            <div className={`suggestions-${theme}`}>
              <div className="suggestions-container">
                <div className="suggestions-close" onClick={() => setSuggestions(false)}>
                  &times;
                </div>
                <div className="wallet-connect-container">
                  <h4>Connect your wallet</h4>
                  <div className="connector-container">
                    {connectors.map(connector => {
                      return (
                        <div key={connector.id} className="wallet-type">
                          <div>
                            <img
                              alt=""
                              src={`/icons/${connector.name.replace(' ', '_').toLowerCase()}.svg`}
                            />{' '}
                            <span className="wallet-connect-name">{connector.name}</span>
                          </div>
                          <Button
                            disabled={!connector.ready}
                            color="three"
                            size="medium"
                            label={
                              isLoading && connector.id === pendingConnector?.id
                                ? 'Connecting'
                                : connectorWagmi
                                ? connector.id === connectorWagmi.id
                                  ? 'Disconnect'
                                  : 'Connect'
                                : 'Connect'
                            }
                            onClick={async () => {
                              connectorWagmi
                                ? connector.id === connectorWagmi.id
                                  ? customDisconnect()
                                  : await customConnect(connector)
                                : await customConnect(connector);
                            }}
                          />
                        </div>
                      );
                    })}
                    {errorWagmi && <div>{errorWagmi.message}</div>}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
